import React from 'react';
import { User as UserIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

interface AvatarProps {
  src?: string;
  alt?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  name?: string;
  colorScheme?: string; // 'blue' | 'purple' | 'emerald' | 'amber' | 'rose'
}

const AVATAR_COLOR_SCHEMES: Record<string, { bg: string; text: string }> = {
  blue: { bg: 'bg-blue-100 dark:bg-blue-900', text: 'text-blue-700 dark:text-blue-200' },
  purple: { bg: 'bg-purple-100 dark:bg-purple-900', text: 'text-purple-700 dark:text-purple-200' },
  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900', text: 'text-emerald-700 dark:text-emerald-200' },
  amber: { bg: 'bg-amber-100 dark:bg-amber-900', text: 'text-amber-700 dark:text-amber-200' },
  rose: { bg: 'bg-rose-100 dark:bg-rose-900', text: 'text-rose-700 dark:text-rose-200' },
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const Avatar: React.FC<AvatarProps> = ({ 
  src, 
  alt = 'User avatar', 
  size = 'md', 
  className = '',
  name,
  colorScheme = 'blue'
}) => {
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-20 w-20'
  };
  
  const iconSizes = {
    sm: 12,
    md: 16,
    lg: 20,
    xl: 28
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-2xl'
  };
  
  if (src && src.trim() !== '') {
    return (
      <img 
        src={src} 
        alt={alt} 
        className={cn('rounded-full object-cover', sizeClasses[size], className)} 
      />
    );
  }

  const initials = name ? getInitials(name) : '';
  
  // Get color scheme classes, defaulting to 'blue' if invalid
  const scheme = AVATAR_COLOR_SCHEMES[colorScheme] || AVATAR_COLOR_SCHEMES.blue;
  
  if (initials) {
    return (
      <div className={cn(
        sizeClasses[size], 
        'rounded-full flex items-center justify-center',
        scheme.bg,
        className
      )}>
        <span className={cn('font-bold', scheme.text, textSizes[size])}>
          {initials}
        </span>
      </div>
    );
  }
  
  return (
    <div className={cn(
      sizeClasses[size], 
      'rounded-full flex items-center justify-center',
      scheme.bg,
      className
    )}>
      <UserIcon 
        size={iconSizes[size]} 
        className={scheme.text}
      />
    </div>
  );
};
